// Closed allowlist for farm-document buckets. These buckets contain internal
// records and are private after migration 193. Keep the bucket names literal in
// the signer table so the static storage guard can audit every signed-URL
// owner; caller-controlled metadata can never steer storage.from().

import type {SupabaseClient} from './supabaseTypes.js';

export const PRIVATE_FARM_DOCUMENT_BUCKETS = Object.freeze([
  'batch-documents',
  'cattle-feed-pdfs',
  'equipment-maintenance-docs',
]);

// What one entry of the signer table is. The result type is a NARROWING of what
// Storage.createSignedUrl actually resolves with — only `data.signedUrl` and
// `error.message` are read below — so the real client stays assignable to it
// while the shape this module depends on is stated explicitly.
type PrivateStorageSigner = (
  client: SupabaseClient,
  path: string,
  expiresIn: number,
) => Promise<{data: {signedUrl: string} | null; error: {message?: string} | null}>;

// Indexed by a caller-supplied bucket string, so the lookup can miss — which is
// exactly the closed-allowlist check both functions below perform. Modelling the
// miss as `undefined` is what makes those checks meaningful to the type checker
// rather than dead code.
const SIGNERS: Readonly<Record<string, PrivateStorageSigner | undefined>> = Object.freeze({
  'batch-documents': (client, path, expiresIn) =>
    client.storage.from('batch-documents').createSignedUrl(path, expiresIn),
  'cattle-feed-pdfs': (client, path, expiresIn) =>
    client.storage.from('cattle-feed-pdfs').createSignedUrl(path, expiresIn),
  'equipment-maintenance-docs': (client, path, expiresIn) =>
    client.storage.from('equipment-maintenance-docs').createSignedUrl(path, expiresIn),
});

const PUBLIC_OBJECT_MARKER = '/storage/v1/object/public/';

// `unknown` rather than `string`: the whole point of the first line is that the
// value arrives from persisted record metadata and may be anything at all.
function cleanPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const path = value.trim().replace(/^\/+/, '');
  return path || null;
}

/**
 * Prefer the canonical path persisted beside every current upload. The URL
 * fallback keeps pre-cutover records readable without trusting an arbitrary
 * host or bucket: only the expected Supabase public-object path is accepted.
 */
export function resolvePrivateStoragePath(bucket: string, path: unknown, legacyUrl: unknown): string | null {
  if (!SIGNERS[bucket]) return null;
  const direct = cleanPath(path);
  if (direct) return direct;
  if (typeof legacyUrl !== 'string' || !legacyUrl.trim()) return null;

  try {
    const parsed = new URL(legacyUrl);
    const prefix = `${PUBLIC_OBJECT_MARKER}${bucket}/`;
    const index = parsed.pathname.indexOf(prefix);
    if (index < 0) return null;
    const encodedPath = parsed.pathname.slice(index + prefix.length);
    return cleanPath(decodeURIComponent(encodedPath));
  } catch {
    return null;
  }
}

/**
 * Mint a short-lived authenticated URL for one allowlisted farm document.
 * The six-hundred-second ceiling is deliberate: callers may request a shorter
 * life, never a durable link that could become stored application data.
 */
export async function createPrivateStorageSignedUrl(
  client: SupabaseClient,
  bucket: string,
  path: unknown,
  expiresIn: number = 600,
): Promise<string> {
  const signer = SIGNERS[bucket];
  if (!signer) throw new Error('Unsupported private Storage bucket.');
  const safePath = cleanPath(path);
  if (!safePath) throw new Error('Private Storage object path is missing.');
  if (!Number.isInteger(expiresIn) || expiresIn < 30 || expiresIn > 600) {
    throw new Error('Private Storage URL lifetime must be between 30 and 600 seconds.');
  }

  const {data, error} = await signer(client, safePath, expiresIn);
  if (error) throw new Error(error.message || 'Could not authorize this file.');
  if (!data?.signedUrl) throw new Error('Storage did not return an authorized file URL.');
  return data.signedUrl;
}
